import { reduce } from "et3-type-guards";
import { memoize } from "lodash";
import { computed, observable, runInAction } from "mobx";
import { apiCallGetDashboardData } from "../../api/dashboard-api/api-call-get-dashboard-data";

import { getSpecificDateWithDays } from "../../core/et3-date/index";
import { E_KHATTABAT_ASSIGN_CLIENT_USER_TYPE, TAlertData } from "../../Types";
import getCorePersonalStore from "../core/core-personal-store";
import { apiCallGetKhattabatDashboardData } from "../../api/dashboard-api/api-call-get-khattabat-dashboard-data";
import { getKhattabatAssignStore } from "../khattabat-stores/assign-store";
import {
  E_SESSION_STORAGE_KEY,
  E_STORAGE_TYPE
} from "../../constants/storage-constants";
import getCoreStorageStore from "../core/core-storage-store";
class DashboardStore {
  fromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 1,
      daysAdded: 0
    })
  );
  toDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  reportsCount = observable.box<number>(0);
  reportedCount = observable.box<number>(0);
  approvalCount = observable.box<number>(0);
  activeTime = observable.box<number>(0);
  investigationCasesCount = observable.box<number>(0);
  totalUnassignedKhattabatClients = observable.box<number>(0);
  totalUnassignedKhattabatTesters = observable.box<number>(0);
  totalMaleUnreadMessages = observable.box<number>(0);
  totalFemaleUnreadMessages = observable.box<number>(0);
  khattabatAdminRemuneration = observable.box<number>(0);
  khattabatAdminCapacity = observable.box<number>(0);

  alertsData = observable.box<TAlertData>({
    newUsersAlert: false,
    fbUsersAlert: false,
    smsUsersAlert: false,
    emailUsersAlert: false,
    lastLoginUsersAlert: false,
    androidPaymentsAlert: false,
    iosPaymentsAlert: false,
    flaggedConnectionAlert: false,
    flaggedConnectionAppAlert: false,
    flaggedConnectionWebAlert: false,
    flaggedConnectionAdminAlert: false,
    rateLimitBlockedAlert: false,
    rateLimitBlockedAppAlert: false,
    rateLimitBlockedWebAlert: false,
    rateLimitBlockedAdminAlert: false,
    alertDate: 0,
    lastAlarmsCheckDateAlert: false,
    sentEmailsAlert: false,
    ipGeolocationAlert: false,
    unsubscribedUserActivityEmailsAlert: false,
    unsubscribedPromotionalEmailsAlert: false,
    unsubscribedNewMessageEmailsAlert: false,
    unsubscribedVisitProfileEmailsAlert: false,
    unsubscribedLikeEmailsAlert: false,
    bpUserAlert: false,
    alertLogs: {
      NEW_USER_LOG_ISSUE: [],
      FB_USER_LOG_ISSUE: [],
      SMS_USER_LOG_ISSUE: [],
      EMAIL_USER_LOG_ISSUE: [],
      LAST_LOGIN_LOG_ISSUE: [],
      ANDROID_PAYMENTS: [],
      IOS_PAYMENTS: [],
      FLAGGED_CONNECTIONS: [],
      RATE_LIMIT: [],
      EMAIL_SENT: [],
      UNSUBSCRIBED_USER_ACTIVITY_EMAILS: [],
      UNSUBSCRIBED_PROMOTIONAL_EMAILS: [],
      UNSUBSCRIBED_NEW_MESSAGE_EMAILS: [],
      UNSUBSCRIBED_VISIT_PROFILE_EMAILS: [],
      UNSUBSCRIBED_LIKE_EMAILS: []
    }
  });

  isAlert = computed(() => {
    const alertLogs = getDashBoardStore().alertsData.get().alertLogs;
    const isAlert =
      reduce(() => alertLogs.EMAIL_USER_LOG_ISSUE.length, 0) > 0 ||
      reduce(() => alertLogs.FB_USER_LOG_ISSUE.length, 0) > 0 ||
      reduce(() => alertLogs.SMS_USER_LOG_ISSUE.length, 0) > 0 ||
      reduce(() => alertLogs.NEW_USER_LOG_ISSUE.length, 0) > 0 ||
      reduce(() => alertLogs.LAST_LOGIN_LOG_ISSUE.length, 0) > 0 ||
      reduce(() => alertLogs.ANDROID_PAYMENTS.length, 0) > 0 ||
      reduce(() => alertLogs.IOS_PAYMENTS.length, 0) > 0 ||
      reduce(() => alertLogs.FLAGGED_CONNECTIONS.length, 0) > 0 ||
      reduce(() => alertLogs.RATE_LIMIT.length, 0) > 0 ||
      reduce(() => alertLogs.EMAIL_SENT.length, 0) > 0 ||
      reduce(() => alertLogs.UNSUBSCRIBED_USER_ACTIVITY_EMAILS.length, 0) > 0 ||
      reduce(() => alertLogs.UNSUBSCRIBED_PROMOTIONAL_EMAILS.length, 0) > 0 ||
      reduce(() => alertLogs.UNSUBSCRIBED_NEW_MESSAGE_EMAILS.length, 0) > 0 ||
      reduce(() => alertLogs.UNSUBSCRIBED_VISIT_PROFILE_EMAILS.length, 0) > 0 ||
      reduce(() => alertLogs.UNSUBSCRIBED_LIKE_EMAILS.length, 0) > 0 ||
      reduce(
        () => getDashBoardStore().alertsData.get().lastAlarmsCheckDateAlert,
        false
      );

    return isAlert;
  });
  returnOneForTrueState = (state: boolean): number => {
    return state ? 1 : 0;
  };
  getAlertsCounts = computed(() => {
    const alertStates = [
      this.alertsData.get().newUsersAlert,
      this.alertsData.get().fbUsersAlert,
      this.alertsData.get().smsUsersAlert,
      this.alertsData.get().emailUsersAlert,
      this.alertsData.get().lastLoginUsersAlert,
      this.alertsData.get().androidPaymentsAlert,
      this.alertsData.get().iosPaymentsAlert,
      this.alertsData.get().flaggedConnectionAlert,
      this.alertsData.get().flaggedConnectionAppAlert,
      this.alertsData.get().flaggedConnectionWebAlert,
      this.alertsData.get().flaggedConnectionAdminAlert,
      this.alertsData.get().rateLimitBlockedAlert,
      this.alertsData.get().rateLimitBlockedAppAlert,
      this.alertsData.get().rateLimitBlockedWebAlert,
      this.alertsData.get().rateLimitBlockedAdminAlert,
      this.alertsData.get().lastAlarmsCheckDateAlert,
      this.alertsData.get().sentEmailsAlert,
      this.alertsData.get().ipGeolocationAlert,
      this.alertsData.get().unsubscribedUserActivityEmailsAlert,
      this.alertsData.get().unsubscribedPromotionalEmailsAlert,
      this.alertsData.get().unsubscribedNewMessageEmailsAlert,
      this.alertsData.get().unsubscribedVisitProfileEmailsAlert,
      this.alertsData.get().unsubscribedLikeEmailsAlert,
      this.alertsData.get().bpUserAlert
    ];
    let count = 0;
    alertStates.forEach((state) => {
      count = count + this.returnOneForTrueState(state);
    });

    return count;
  });
  decrementUnassignedNewClients = () => {
    const selectedClientType =
      getKhattabatAssignStore().selectedClientType.get();
    if (selectedClientType === E_KHATTABAT_ASSIGN_CLIENT_USER_TYPE.REAL) {
      this.totalUnassignedKhattabatClients.set(
        this.totalUnassignedKhattabatClients.get() - 1
      );
    } else {
      this.totalUnassignedKhattabatTesters.set(
        this.totalUnassignedKhattabatTesters.get() - 1
      );
    }
  };
  getDashboardData = async () => {
    const { data, status } = await apiCallGetDashboardData();
    if (status === "OK") {
      getCoreStorageStore(E_STORAGE_TYPE.SESSION).set(
        E_SESSION_STORAGE_KEY.COUNTS,
        {
          reportsCount: data.reportsCount,
          reportedCount: data.reportedCount,
          approvalCount: data.approvalDataCount,
          investigationCasesCount: data.investigationCasesCount,
          activeTime: data.activeTime
        }
      );
      this.reportsCount.set(data.reportsCount);
      this.reportedCount.set(data.reportedCount);
      this.approvalCount.set(data.approvalDataCount);
      this.investigationCasesCount.set(data.investigationCasesCount);
      this.activeTime.set(data.activeTime);
      this.alertsData.set(data.alertsData);
    }
  };

  getKhattabatDashboardData = async () => {
    const { data, status } = await apiCallGetKhattabatDashboardData();
    if (status === "OK") {
      getCoreStorageStore(E_STORAGE_TYPE.SESSION).set(
        E_SESSION_STORAGE_KEY.COUNTS,
        {
          adminAlkhattabatCapacity: data.adminAlKhattabatCapacity,
          totalUnassignedKhattabatClients: data.totalUnassignedKhattabatClients,
          totalUnassignedKhattabatTesters: data.totalUnassignedKhattabatTesters,
          totalMaleUnreadMessages: data.totalUnreadMaleKhattabatMessages,
          totalFemaleUnreadMessages: data.totalUnreadFemaleKhattabatMessages,
          khattabatAdminRemuneration: data.khattabatAdminRemuneration
        }
      );
      runInAction(() => {
        this.totalUnassignedKhattabatClients.set(
          data.totalUnassignedKhattabatClients
        );
        this.totalUnassignedKhattabatTesters.set(
          data.totalUnassignedKhattabatTesters
        );
        this.totalMaleUnreadMessages.set(data.totalUnreadMaleKhattabatMessages);
        this.totalFemaleUnreadMessages.set(
          data.totalUnreadFemaleKhattabatMessages
        );
        this.khattabatAdminRemuneration.set(data.khattabatAdminRemuneration);
        this.khattabatAdminCapacity.set(data.adminAlKhattabatCapacity);
      });
    }
  };

  getIsAlert = computed(() => {
    const isAnyKhattabaAdmin = getCorePersonalStore().isAnyKhattabaAdmin.get();
    return this.isAlert.get() && !isAnyKhattabaAdmin;
  });
}

const getDashBoardStore = memoize(
  () => new DashboardStore(),
  () => 1
);
export default getDashBoardStore;
